import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Chip, IconButton, Button, CircularProgress, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import { getPatient, getMedicalRecords, getDoctors, getPatientProblems, downloadMedicalHistoryPdf, getMyClinic, downloadClinicLogo } from '../services/api';
import { ConsultasTab } from '../components/MedicalRecordPanel';
import { PatientSidebar } from '../components/patient-profile/PatientSidebar';
import { printHC } from '../components/patient-profile/patientProfileHelpers';
import PatientAvatar from '../components/PatientAvatar';
import { useAuth } from '../hooks/useAuth';
import { useClinicFeatures } from '../hooks/useClinicFeatures';
import { useNotification } from '../hooks/useNotification';

const RECORDS_PAGE_SIZE = 30;

// ── Página principal ──────────────────────────────────────────────────────────
const PatientProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMedico, isAdmin, isRecepcionista } = useAuth();
  const { features } = useClinicFeatures();
  const notify = useNotification();
  const canCreateRecord = isMedico || isAdmin;
  const canEditPhoto = isAdmin || isRecepcionista || isMedico;

  const [patient, setPatient]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [sideTab, setSideTab]         = useState(0);
  const [records, setRecords]         = useState([]);
  const [problems, setProblems]       = useState([]);
  const [doctors, setDoctors]         = useState([]);
  const [activeProblem, setActiveProblem] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [clinic, setClinic] = useState(null);
  // Consultas cargadas en la pestaña se traen de a lotes ("Cargar más") en
  // vez de todas de una — un historial largo tarda en renderizar como
  // accordions y no aporta nada mostrar 200 consultas colapsadas de
  // entrada. hasMoreRecords se infiere de que el último lote haya venido
  // completo (== RECORDS_PAGE_SIZE): no hay "total" en la respuesta del
  // backend, así que es la única señal disponible sin cambiar el schema.
  const [hasMoreRecords, setHasMoreRecords] = useState(false);
  const [loadingMoreRecords, setLoadingMoreRecords] = useState(false);

  // Llegada desde "Llamar paciente" en la sala de espera: pre-vincula la
  // consulta nueva al turno correspondiente en vez de dejarla suelta.
  const [newRecordContext, setNewRecordContext] = useState(null); // { appointmentId, doctorId, appointmentNote }

  useEffect(() => {
    const incomingAppointmentId = location.state?.newRecordAppointmentId;
    if (incomingAppointmentId) {
      setNewRecordContext({
        appointmentId: incomingAppointmentId,
        doctorId: location.state?.newRecordDoctorId || null,
        appointmentNote: location.state?.newRecordAppointmentNote || null,
      });
      setShowNewForm(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);

  const loadPatient = useCallback(async () => {
    try { const res = await getPatient(id); setPatient(res.data); }
    catch { navigate('/patients', { replace: true }); }
    finally { setLoading(false); }
  }, [id, navigate]);

  const loadRecords = useCallback(async () => {
    try {
      const res = await getMedicalRecords(id, { skip: 0, limit: RECORDS_PAGE_SIZE });
      const items = res.data || [];
      setRecords(items);
      setHasMoreRecords(items.length === RECORDS_PAGE_SIZE);
    } catch {}
  }, [id]);

  const loadMoreRecords = async () => {
    setLoadingMoreRecords(true);
    try {
      const res = await getMedicalRecords(id, { skip: records.length, limit: RECORDS_PAGE_SIZE });
      const items = res.data || [];
      setRecords(prev => [...prev, ...items]);
      setHasMoreRecords(items.length === RECORDS_PAGE_SIZE);
    } catch (err) {
      notify(err.userMessage || 'Error al cargar más consultas', 'error');
    } finally {
      setLoadingMoreRecords(false);
    }
  };

  const loadProblems = useCallback(async () => {
    try { const res = await getPatientProblems(id); setProblems(res.data || []); } catch {}
  }, [id]);

  useEffect(() => {
    loadPatient(); loadRecords(); loadProblems();
    getDoctors({ limit: 500 }).then(r => setDoctors(r.data?.items || [])).catch(() => {});
    getMyClinic().then(r => setClinic(r.data)).catch(() => {});
  }, [loadPatient, loadRecords, loadProblems]);

  const handleRecordSaved = () => { setShowNewForm(false); setNewRecordContext(null); loadRecords(); };

  const handlePrintHC = async () => {
    setExportMenuAnchor(null);
    // "Imprimir" siempre trae TODAS las consultas directo del backend,
    // independiente de cuántas haya cargado el usuario en pantalla con
    // "Cargar más" — igual que ya hace "Descargar PDF". Depender del
    // `records` paginado del estado dejaría la impresión incompleta sin
    // que nadie lo notara, un riesgo real en un documento clínico.
    let allRecords = records;
    try {
      const res = await getMedicalRecords(id, { limit: 500 });
      allRecords = res.data || [];
    } catch {
      notify('No se pudo traer el historial completo — se imprime con lo cargado en pantalla', 'warning');
    }
    // El logo vive atrás de un endpoint autenticado — un <img src> directo
    // en la ventana nueva de impresión no manda el header de Authorization,
    // así que se trae como blob autenticado y se convierte a data URI antes
    // de armar el HTML.
    let logoDataUrl = null;
    if (clinic?.has_logo) {
      try {
        const res = await downloadClinicLogo();
        logoDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(res.data);
        });
      } catch {
        // logo opcional — si falla, se imprime igual sin logo
      }
    }
    printHC(patient, allRecords, clinic, logoDataUrl);
  };

  const handleDownloadPdf = async () => {
    setExportMenuAnchor(null);
    setDownloadingPdf(true);
    try {
      const res = await downloadMedicalHistoryPdf(id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `HC_${patient.name.replace(/\s+/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      notify(err.userMessage || 'No se pudo descargar la Historia Clínica', 'error');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }
  if (!patient) return null;

  return (
    <Box>
      {/* Header — en mobile el botón de acción baja a su propia fila
          completa (en vez de competir por espacio con nombre/chip).
          "Nueva consulta" NO tiene botón acá: vive junto al formulario en
          ConsultasTab (más abajo), que está siempre visible al costado —
          tener los dos botones a la vez era una duplicación real, no dos
          funciones distintas. showNewForm sigue existiendo para abrir el
          formulario automáticamente al llegar desde "Llamar paciente" en
          sala de espera (ver newRecordContext). */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {/* Antes volvía siempre a /patients sin importar de dónde vino —
              un médico que llegaba desde la Agenda (vía un turno) terminaba
              en la lista de pacientes en vez de volver a la Agenda donde
              estaba. navigate(-1) usa el historial real; location.key
              === 'default' significa que esta pestaña no tiene historial
              propio (entró por un link directo), caso en el que -1 no
              tiene a dónde ir y se cae a /patients como antes. Pedido
              explícito del usuario 2026-11-02. */}
          <IconButton
            onClick={() => (location.key === 'default' ? navigate('/patients') : navigate(-1))}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <PatientAvatar patient={patient} size={40} editable={canEditPhoto} onChanged={loadPatient} />
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1, minWidth: 0 }} noWrap>
            {patient.name}
          </Typography>
          {/* Solo se muestra cuando aporta información (paciente dado de
              baja) — el caso normal es estar activo. */}
          {patient.is_active === false && (
            <Chip label="Inactivo" size="small" color="default" />
          )}
        </Box>
        {canCreateRecord && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
            <Button size="small" startIcon={downloadingPdf ? <CircularProgress size={14} /> : <PrintIcon sx={{ fontSize: 15 }} />} variant="outlined"
              onClick={(e) => setExportMenuAnchor(e.currentTarget)}
              disabled={records.length === 0 || downloadingPdf}
              sx={{ flex: { xs: 1, sm: 'initial' } }}>
              Exportar HC
            </Button>
            <Menu anchorEl={exportMenuAnchor} open={Boolean(exportMenuAnchor)} onClose={() => setExportMenuAnchor(null)}>
              <MenuItem dense onClick={handlePrintHC}>
                <ListItemIcon><PrintIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Imprimir</ListItemText>
              </MenuItem>
              <MenuItem dense onClick={handleDownloadPdf}>
                <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Descargar PDF</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Box>

      {/* Layout dos columnas — recepcionista no ve Historia Clínica (Resumen/Consultas) */}
      <Box sx={{
        display: 'flex', gap: 2, alignItems: 'flex-start',
        flexDirection: { xs: 'column', md: 'row' },
      }}>
        {/* Sidebar */}
        <PatientSidebar
          patient={patient}
          onUpdated={() => { loadPatient(); loadProblems(); }}
          sideTab={sideTab}
          setSideTab={setSideTab}
          features={features}
          records={records}
          problems={problems}
          activeProblem={activeProblem}
          onProblemClick={setActiveProblem}
          doctors={doctors}
          fullWidth={!canCreateRecord}
        />

        {/* Área principal: Consultas — solo admin/médico */}
        {canCreateRecord && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ConsultasTab
              records={records}
              patient={patient}
              onRecordSaved={handleRecordSaved}
              showFormInitially={showNewForm}
              activeProblem={activeProblem}
              onClearProblem={() => setActiveProblem(null)}
              doctors={doctors}
              problems={problems}
              initialAppointmentId={newRecordContext?.appointmentId || null}
              initialDoctorId={newRecordContext?.doctorId || null}
              initialAppointmentNote={newRecordContext?.appointmentNote || null}
              hasMoreRecords={hasMoreRecords}
              loadingMoreRecords={loadingMoreRecords}
              onLoadMoreRecords={loadMoreRecords}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PatientProfile;
